import { MarketInformationAssetDataExpanded, OracleType } from "@bridgesplit/abf-sdk";
import { BsMetaUtil, useTokenListMetadataByMint, useTokenListMetadataByMints } from "@bridgesplit/abf-react";
import { Row, StatProps, AddressText, Text, TextVariant } from "@bridgesplit/ui";
import { combineCollections, filterTruthy, filterUndefined, removeDuplicates } from "@bridgesplit/utils";

import { ORACLE_META } from "../common/pricing";

// topLevelOracleStats refers to the primary oracles directly listed on the asset.
// These are displayed above the `customOracleStats`, which represent the underlying oracles
interface OracleStats {
    topLevelOracleStats: StatProps[];
    customOracleStats: StatProps[];
}

export function getOracleTypes({
    collateralMarketData,
    principalMarketData
}: {
    collateralMarketData: MarketInformationAssetDataExpanded | undefined;
    principalMarketData: MarketInformationAssetDataExpanded | undefined;
}): OracleType[] {
    const principalOracleAccount = principalMarketData?.oracleType;
    const collateralOracleAccount = collateralMarketData?.oracleType;

    const principalCustomOracleAccounts = principalMarketData?.customOracleMapping?.map((m) => m.oracleType);
    const collateralCustomOracleAccounts = collateralMarketData?.customOracleMapping?.map((m) => m.oracleType);

    const combinedOracleAccounts = combineCollections([principalCustomOracleAccounts, collateralCustomOracleAccounts]);

    const uniqueOracleAccounts = removeDuplicates([
        principalOracleAccount,
        collateralOracleAccount,
        ...combinedOracleAccounts
    ]);

    return uniqueOracleAccounts.filter(filterUndefined);
}

export function useOracleStats({
    collateralMarketData,
    principalMarketData,
    variant
}: {
    collateralMarketData: MarketInformationAssetDataExpanded | undefined;
    principalMarketData: MarketInformationAssetDataExpanded | undefined;
    variant: TextVariant;
}): OracleStats | undefined {
    // Main metadata
    const collateralMetadata = useTokenListMetadataByMint(collateralMarketData?.assetIdentifier);
    const principalMetadata = useTokenListMetadataByMint(principalMarketData?.assetIdentifier);

    // Collect all oracle accounts that need metadata

    const principalOracleAccountMetadata = principalMarketData?.customOracleMapping?.map((m) => m.assetIdentifier);
    const collateralOracleAccountMetadata = collateralMarketData?.customOracleMapping?.map((m) => m.assetIdentifier);

    const metadata = combineCollections([principalOracleAccountMetadata, collateralOracleAccountMetadata]);

    const { getMetadata } = useTokenListMetadataByMints(metadata);

    const topLevelPrincipalName = BsMetaUtil.getName(principalMetadata);
    const topLevelCollateralName = BsMetaUtil.getName(collateralMetadata);

    if (!principalMarketData || !collateralMarketData) return undefined;

    const topLevelOracleStats = [
        {
            key: "top-principal-oracle",
            caption: (
                <Text color="caption" variant={variant}>
                    {topLevelPrincipalName}
                </Text>
            ),
            value: (
                <OracleTagAddress
                    oracleAccount={principalMarketData.oracleAccount}
                    oracleType={principalMarketData.oracleType}
                    variant={variant}
                />
            )
        },
        {
            key: "top-collateral-oracle",
            caption: (
                <Text color="caption" variant={variant}>
                    {topLevelCollateralName}
                </Text>
            ),
            value: (
                <OracleTagAddress
                    oracleAccount={collateralMarketData.oracleAccount}
                    oracleType={collateralMarketData.oracleType}
                    variant={variant}
                />
            )
        }
    ];

    const combinedCustomOracleStats = combineCollections([
        principalMarketData.customOracleMapping,
        collateralMarketData.customOracleMapping
    ])
        .map((m) => {
            if (
                m.oracleAccount === principalMarketData?.oracleAccount ||
                m.oracleAccount === collateralMarketData.oracleAccount
            ) {
                return null;
            }
            const metadata = getMetadata(m.assetIdentifier);
            const name = BsMetaUtil.getName(metadata);

            return {
                key: m.oracleAccount,
                caption: name,
                value: <OracleTagAddress oracleAccount={m.oracleAccount} oracleType={m.oracleType} variant={variant} />
            };
        })
        .filter(filterTruthy);

    return {
        topLevelOracleStats: topLevelOracleStats,
        customOracleStats: combinedCustomOracleStats
    };
}

function OracleTagAddress({
    oracleAccount,
    oracleType,
    variant
}: {
    oracleAccount: string;
    oracleType: OracleType;
    variant: TextVariant;
}) {
    if (!oracleType) return null;

    const meta = ORACLE_META[oracleType as OracleType];
    if (!meta) return null;
    return (
        <Row>
            <Text
                sx={{
                    whiteSpace: "nowrap"
                }}
                variant={variant}
            >
                {meta.name}
            </Text>
            <AddressText showText={false} address={oracleAccount} />
        </Row>
    );
}
